// UnskipStageDialog.tsx
import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    TextField,
    Stack,
    Box,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import { LoadingButton } from "@mui/lab";
import { ProcessStageOverview, unskipStage } from "../validationProcessApi";
import { useAppDispatch } from "../../../../app/hooks";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { CancelButton } from "../../../../app/components/CancelButton";

type Props = {
    processId: string;
    stage: ProcessStageOverview | null;
    onClose: () => void;
    onSuccess: (stage: ProcessStageOverview) => void;
};

export const UnskipStageDialog: React.FC<Props> = ({
    processId,
    stage,
    onClose,
    onSuccess,
}) => {
    const dispatch = useAppDispatch();

    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);

    const handleClose = () => {
        if (saving) return;
        setServerError(null);
        setComment("");
        onClose();
    };

    const handleConfirm = async () => {
        if (!stage) return;

        setSaving(true);
        setServerError(null);

        try {
            const result = await dispatch(
                unskipStage({
                    processId,
                    stageId: stage.id,
                    comment,
                })
            ).unwrap();

            dispatch(showSuccess("saved"));
            onSuccess(result);
        } catch (e) {
            setServerError("error");
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    const stageLabel = stage?.stage || "etap";

    return (
        <Dialog open={true} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[200],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 0.75,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <ReplayIcon color="primary" />
                    <Box>
                        Cofnij pominięcie etapu: <strong>{stageLabel}</strong>
                    </Box>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <DialogContentText sx={{ mb: 2 }}>
                    Czy na pewno chcesz cofnąć pominięcie etapu{" "}
                    <strong>{stageLabel}</strong>?{" "}
                    Jeśli etap był pominięty, a dokumenty zostały anulowane w wyniku
                    pominięcia, system przywróci je przywrócić do poprzedniego statusu.
                </DialogContentText>

                <TextField
                    label="Komentarz (opcjonalnie)"
                    placeholder="Opcjonalnie podaj komentarz do cofnięcia pominięcia etapu..."
                    fullWidth
                    multiline
                    minRows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />

                {serverError && (
                    <Box mt={2} sx={{ color: (t) => t.palette.error.main, fontSize: 14 }}>
                        {serverError}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    loading={saving}
                    onClick={handleConfirm}
                >
                    Cofnij pominięcie
                </LoadingButton>

                <CancelButton loading={saving} onClick={handleClose} />
            </DialogActions>
        </Dialog>
    );
};
